import { useState, useEffect, useCallback, useRef } from 'react';
import { getPlaces } from '../services/api';

/**
 * Постраничная выдача мест.
 *
 * Отдельный хук, а не режим `usePlaces`: тот отдаёт список целиком, без
 * страниц. После перевода главной он остался без потребителей — не удалён
 * намеренно, это выходит за рамки правки главной.
 *
 * Первая страница грузится сразу, дальше — `loadMore()`. Признак конца
 * берём из `meta.pagination`, а не из длины ответа: короткая страница
 * может прийти и при отфильтрованных записях.
 */
export const usePaginatedPlaces = (pageSize = 12) => {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);      // первая страница
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);

  // Страница, которую уже загрузили. В ref, а не в state: loadMore не должен
  // пересоздаваться на каждую подгрузку, иначе IntersectionObserver
  // переподписывается и стреляет повторно
  const pageRef = useRef(0);
  const loadingRef = useRef(false);
  const mountedRef = useRef(true);

  const fetchPage = useCallback(async (page) => {
    // Защита от параллельных вызовов: наблюдатель может сработать дважды,
    // пока запрос в пути
    if (loadingRef.current) return;
    loadingRef.current = true;

    // loading — строго про первую страницу. Подгрузка идёт через loadingMore
    // и показывается внутри ленты: если поднять loading повторно, PageLoader
    // подменит собой всю страницу и скролл улетит наверх
    if (page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await getPlaces({
        'pagination[page]': page,
        'pagination[pageSize]': pageSize,
      });

      if (!mountedRef.current) return;

      const batch = response.data || [];
      const pagination = response.meta?.pagination;

      setPlaces((prev) => {
        // Страховка от дублей, если запрос всё же повторился
        if (page === 1) return batch;
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...batch.filter((p) => !seen.has(p.id))];
      });

      pageRef.current = page;
      setHasMore(pagination ? page < pagination.pageCount : batch.length === pageSize);
      setError(null);
    } catch (err) {
      if (mountedRef.current) setError(err.message);
    } finally {
      loadingRef.current = false;
      if (mountedRef.current) {
        // Симметрично установке: гасим ровно тот флаг, что поднимали
        if (page === 1) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    }
  }, [pageSize]);

  useEffect(() => {
    mountedRef.current = true;
    pageRef.current = 0;
    fetchPage(1);
    return () => {
      mountedRef.current = false;
    };
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (loadingRef.current || !hasMore) return;
    fetchPage(pageRef.current + 1);
  }, [fetchPage, hasMore]);

  return { places, loading, loadingMore, hasMore, loadMore, error };
};
